import React from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  SectionContextProvider,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { PropertyInfoPanel, addressGroupDef } from './PropertyInfoPanel';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Component
// ============================================================================

export function ScheduleTourForm() {
  const prefix = 'scheduletourform';
  const formBlockPath = `${prefix}.form_block`;

  const formSlot = useBlockSlot(formBlockPath, {
    defaultValue: 'multihub-tour-scheduler',
  });

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="cf-section">
      <div ref={sectionRef} className="reveal-fade-up cf-container">
        <div className="cf-grid">
          {/* Left column: property info panel */}
          <div className="cf-info-col">
            <PropertyInfoPanel prefix={prefix} />
          </div>

          {/* Right column: form block */}
          <div className="cf-form-col">
            <EditableToken path={formBlockPath} as="div">
              {formSlot ? (
                <SectionContextProvider sectionId={formSlot.sectionId}>
                  <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                </SectionContextProvider>
              ) : (
                <div className="cf-form-card">
                  <p className="cf-form-empty">No form block selected</p>
                </div>
              )}
            </EditableToken>
          </div>
        </div>
      </div>

      {/* Schema-lint anchors — static paths so the linter can match each schema key.
          The live EditableTokens in PropertyInfoPanel use dynamic paths from prefix. */}
      <EditableToken path="scheduletourform.form_block"   as="div"  className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduletourform.visit_label"  as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableSlotGroup groupPath="scheduletourform.address" definition={addressGroupDef} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableToken path="scheduletourform.call_label"   as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduletourform.phone"        as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduletourform.hours_label"  as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>

      <style>{`
        .cf-section {
          padding: 2.5rem 0 3.5rem;
          background: white;
        }
        @media (min-width: 768px) {
          .cf-section { padding: 3rem 0 4rem; }
        }
        .cf-container {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px)  { .cf-container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .cf-container { padding: 0 2rem; } }

        .cf-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
        }
        @media (min-width: 1024px) {
          .cf-grid {
            grid-template-columns: 5fr 7fr;
            gap: 2.5rem;
            align-items: start;
          }
        }

        /* ===== Form column ===== */
        .cf-form-col { position: relative; }
        .cf-form-card {
          background: white;
          border-radius: var(--vibe-radius-lg, 12px);
          border: 1px solid var(--vibe-color-border, #e5e7eb);
          padding: 2rem;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
        }
        .cf-form-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          text-align: center;
          margin: 0;
          padding: 2rem 0;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ScheduleTourForm',
  displayName: 'Schedule A Tour Form',
  tokens: {
    'scheduletourform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-tour-scheduler',
      acceptedBlockCategories: ['Forms'],
    },
    'scheduletourform.visit_label': {
      label: 'Visit Us Label',
      type: 'text',
      defaultValue: 'Visit Us',
    },
    'scheduletourform.call_label': {
      label: 'Call Us Label',
      type: 'text',
      defaultValue: 'Call Us',
    },
    'scheduletourform.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '',
    },
    'scheduletourform.hours_label': {
      label: 'Office Hours Label',
      type: 'text',
      defaultValue: 'Office Hours',
    },
  },
  slotGroups: {
    'scheduletourform.address': {
      label: 'Address',
      fields: {
        text: {
          label: 'Address Text',
          type: 'text',
          defaultField: 'property_full_address',
          defaultValue: '',
        },
        url: {
          label: 'Map URL',
          type: 'url',
          // URL has no defaultField — defaults to usePropertyAddressUrl() at render
          // time. Editor overrides still win.
          defaultValue: '',
        },
        _target: {
          label: 'Target',
          type: 'text',
          defaultValue: '_blank',
        },
      },
    },
  },
  collectionBindings: {
    'scheduletourform.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ScheduleTourForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ScheduleTourForm;
